import React, { useState } from 'react';
import { setMyMagicLinkRequirement, setMyTwoFactorMethod } from '../api/appMagicLinkApi';
import { resolveTwoFactorPhone, maskTwoFactorPhone } from '../../../utils/twoFactorPhone';
import '../styles/twoFactorSettings.css';

export default function TwoFactorSettings({ user, onUserUpdate, variant = 'default' }) {
  const embedded = variant === 'embedded';
  const enabled = user?.requireMagicLink === true;
  const method = user?.twoFactorMethod === 'sms' ? 'sms' : 'email';
  const savedPhone = resolveTwoFactorPhone(user);
  const [saving, setSaving] = useState(false);
  const [savingMethod, setSavingMethod] = useState(false);
  const [error, setError] = useState('');

  const handleToggle = async (e) => {
    const next = Boolean(e.target.checked);
    setError('');
    setSaving(true);
    try {
      await setMyMagicLinkRequirement({ requireMagicLink: next });
      onUserUpdate?.({ requireMagicLink: next });
    } catch (err) {
      setError(err?.message || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleMethodChange = async (e) => {
    const nextMethod = e.target.value === 'sms' ? 'sms' : 'email';
    setError('');
    setSavingMethod(true);
    try {
      await setMyTwoFactorMethod({ method: nextMethod });
      onUserUpdate?.({ twoFactorMethod: nextMethod });
    } catch (err) {
      setError(err?.message || 'Failed to update 2FA method');
    } finally {
      setSavingMethod(false);
    }
  };

  return (
    <div
      className={`tfSettings${embedded ? ' tfSettings--embedded' : ''}`}
      aria-busy={saving || savingMethod ? 'true' : 'false'}
    >
      {error ? (
        <div className="tfSettings__alert tfSettings__alert--error" role="alert">
          {error}
        </div>
      ) : null}

      {!embedded && !enabled ? (
        <div className="tfSettings__alert tfSettings__alert--warn" role="status">
          2FA is currently disabled. Enable it to better protect your account from unauthorized access.
        </div>
      ) : null}

      <label className="tfSettings__checkbox" htmlFor="requireMagicLinkCheckbox">
        <input
          id="requireMagicLinkCheckbox"
          type="checkbox"
          checked={enabled}
          onChange={handleToggle}
          disabled={saving}
        />
        <span className="tfSettings__checkboxLabel">
          <span className="tfSettings__title">Require 2FA on login</span>
          <span className="tfSettings__sub">
            {enabled
              ? 'Signing in requires an email link or SMS code after your password.'
              : 'Add a second step after your password for stronger account protection.'}
          </span>
        </span>
      </label>

      {enabled ? (
        <div className="tfSettings__methodBlock">
          <label className="tfSettings__fieldLabel" htmlFor="twoFactorMethodSelect">
            Delivery method
          </label>
          <select
            id="twoFactorMethodSelect"
            className="tfSettings__select"
            value={method}
            onChange={handleMethodChange}
            disabled={savingMethod}
            aria-label="2FA delivery method"
          >
            <option value="email">Email login link</option>
            <option value="sms">SMS login code</option>
          </select>
          <p className="tfSettings__sub">
            {method === 'sms'
              ? savedPhone
                ? `Codes are sent to ${maskTwoFactorPhone(savedPhone)}.`
                : 'Add your mobile number in Profile, then save, before using SMS.'
              : 'A sign-in link is sent to your email after you enter your password.'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
